// A director or owner Platforma365 refused as a duplicate, side by side with
// the profile it already holds, and the staff override that attaches it
// (DEV-126). Mirrors "Attach and continue" for an existing company.
//
// The provider's candidate ids come from its own refusal, so nobody hunts for
// them in the console. Admin and AML/MLRO only; the API enforces it as well.

import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import {
  customersApi,
  type AttachExistingPersonResult,
  type DuplicatePersonRefusal,
} from '../api/customers.api.js';
import { useAuth } from '../auth/useAuth.js';
import {
  MIN_ATTACH_REASON_LENGTH,
  PROVIDER_PERSON_ID_RE,
  STATUS_LABEL,
  canAttach,
  comparisonRows,
  type ComparisonStatus,
} from './duplicate-person-attach.logic.js';

const STATUS_COLOR: Readonly<Record<ComparisonStatus, 'success' | 'error' | 'warning' | 'default'>> = {
  match: 'success',
  mismatch: 'error',
  swapped: 'warning',
  'not-compared': 'default',
};

function roleLabel(role: string): string {
  return role === 'UBO' ? 'Beneficial owner' : 'Director';
}

interface RefusalCardProps {
  customerId: string;
  refusal: DuplicatePersonRefusal;
  mayAttach: boolean;
  onAttached: () => Promise<unknown> | void;
}

function RefusalCard({ customerId, refusal, mayAttach, onAttached }: RefusalCardProps): JSX.Element {
  const candidate = refusal.candidates[0];
  const [profileId, setProfileId] = useState(
    refusal.attachedProviderId !== '' ? refusal.attachedProviderId : (candidate?.providerId ?? ''),
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AttachExistingPersonResult | null>(null);

  const consoleUrl =
    refusal.candidates.find((c) => c.providerId === profileId)?.consoleUrl ?? candidate?.consoleUrl ?? '';
  const rows = refusal.comparison === null ? [] : comparisonRows(refusal.comparison);
  const swapped = rows.find((row) => row.status === 'swapped');

  const attach = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const res = await customersApi.attachExistingPerson(customerId, {
        refusalId: refusal.id,
        providerPersonId: profileId.trim(),
        role: refusal.role,
        reason: reason.trim(),
      });
      setResult(res);
      await onAttached();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not attach the profile.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      data-testid={`duplicate-refusal-${refusal.id}`}
      sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, p: 1.5, mb: 1.5 }}
    >
      <Typography variant="subtitle2">
        {refusal.personName} · {roleLabel(refusal.role)} — refused as a duplicate at Platforma365
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Not linked automatically: {refusal.reason}.
      </Typography>

      {swapped !== undefined ? (
        <Alert severity="warning" sx={{ mb: 1 }} role="alert">
          {swapped.note}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <Typography variant="body2">
          Platforma365 profile{refusal.candidates.length === 1 ? '' : 's'} named in the refusal:{' '}
          {refusal.candidates.map((c) => c.providerId).join(', ') || 'none'}
        </Typography>
        {consoleUrl !== '' ? (
          <Link href={consoleUrl} target="_blank" rel="noreferrer noopener" variant="body2">
            Open in Platforma365
          </Link>
        ) : null}
      </Stack>

      {rows.length > 0 ? (
        <Table size="small" aria-label={`Comparison for ${refusal.personName}`} sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Application</TableCell>
              <TableCell>Platforma365</TableCell>
              <TableCell>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell component="th" scope="row">
                  {row.label}
                </TableCell>
                <TableCell>{row.applicant}</TableCell>
                <TableCell>{row.provider}</TableCell>
                <TableCell>
                  <Chip size="small" color={STATUS_COLOR[row.status]} label={STATUS_LABEL[row.status]} />
                  {row.note !== '' && row.status !== 'swapped' ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.note}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          No single profile was read, so there is nothing to compare here. Open each candidate in
          Platforma365 before choosing one.
        </Typography>
      )}

      {result !== null ? (
        <Alert severity={result.pushError === '' ? 'success' : 'warning'} sx={{ mb: 1 }}>
          Attached Platforma365 profile {result.providerId} to {result.personName}.
          {result.pushError === ''
            ? ' The application was sent again.'
            : ` The application could not be completed: ${result.pushError}`}
          {result.verificationLink !== '' ? ` Identity-check link: ${result.verificationLink}` : ''}
          {result.linkError !== '' ? ` The link was not issued: ${result.linkError}` : ''}
        </Alert>
      ) : null}
      {error !== '' ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}

      {mayAttach ? (
        <Stack spacing={1} sx={{ maxWidth: 620 }}>
          <Typography variant="body2">
            If this is the same person, attach the existing profile. Norvix checks the ID exists
            and belongs to no other customer, and never changes the provider&apos;s record.
          </Typography>
          <TextField
            size="small"
            label="Platforma365 person ID"
            value={profileId}
            onChange={(event) => setProfileId(event.target.value.trim())}
            error={profileId !== '' && !PROVIDER_PERSON_ID_RE.test(profileId)}
          />
          <TextField
            size="small"
            label="Reason (required)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            helperText={`At least ${String(MIN_ATTACH_REASON_LENGTH)} characters. Written to the case file with your name.`}
          />
          <Box>
            <Button
              variant="contained"
              disabled={busy || !canAttach(profileId, reason)}
              onClick={() => void attach()}
            >
              {busy ? 'Attaching…' : 'Attach existing person and continue'}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Only Admin and AML/MLRO can attach an existing provider profile.
        </Typography>
      )}
    </Box>
  );
}

export function DuplicatePersonAttachPanel({
  customerId,
  refusals,
  onAttached,
}: {
  customerId: string;
  refusals: readonly DuplicatePersonRefusal[];
  onAttached: () => Promise<unknown> | void;
}): JSX.Element | null {
  const { can } = useAuth();
  if (refusals.length === 0) return null;
  const mayAttach = can('kyc.decide');
  return (
    <Box sx={{ mb: 1.5 }}>
      {refusals.map((refusal) => (
        <RefusalCard
          key={refusal.id}
          customerId={customerId}
          refusal={refusal}
          mayAttach={mayAttach}
          onAttached={onAttached}
        />
      ))}
    </Box>
  );
}
